import { createBrowserRouter, Navigate } from "react-router-dom";

import {
  AdminAuditPage,
  AdminLayout,
  AdminOverviewPage,
  AdminUsersPage,
  AdminWorkspacesPage,
} from "@/features/admin";
import AppHome from "./AppHome";
import AppLayout from "./AppLayout";
import { BacklogPage } from "@/features/backlog";
import { ReportsPage } from "@/features/reports";
import { BoardPage } from "@/features/board";
import { ChatPage } from "@/features/chat";
import {
  NotificationSettingsPage,
  NotificationsPage,
} from "@/features/notifications";
import { IssuePage } from "@/features/issue";
import { ProjectSettingsPage } from "@/features/project-settings";
import ForgotPasswordPage from "./ForgotPasswordPage";
import InvitationsPage from "./InvitationsPage";
import InvitePage from "./InvitePage";
import LoginPage from "./LoginPage";
import NewWorkspacePage from "./NewWorkspacePage";
import NotFound from "./NotFound";
import ProjectPage from "./ProjectPage";
import RegisterPage from "./RegisterPage";
import ResetPasswordPage from "./ResetPasswordPage";
import {
  SimulatedCheckoutPage,
  SimulatedPortalPage,
} from "./SimulatedBillingPage";
import {
  RequireAnonymous,
  RequireAuth,
  RequirePlatformAdmin,
  RootLayout,
  RouteErrorPage,
  WorkspaceGate,
  WorkspaceRedirect,
} from "./guards";
import AccountPage from "./settings/AccountPage";
import AIPage from "./settings/AIPage";
import BillingPage from "./settings/BillingPage";
import GeneralSettingsPage from "./settings/GeneralSettingsPage";
import LabelsPage from "./settings/LabelsPage";
import MembersPage from "./settings/MembersPage";
import SettingsLayout from "./settings/SettingsLayout";
import VoiceSettingsPage from "./settings/VoiceSettingsPage";

/**
 * The route table.
 *
 * Three tiers, in this order:
 *
 *  1. public — sign in, sign up, password recovery, and the invitation link
 *     (which must render its preview before anyone has an account);
 *  2. authenticated but workspace-less — the invitation inbox, workspace
 *     creation, and the stub billing landings;
 *  3. `/:slug/*` — everything inside a workspace, gated on a membership the
 *     session actually carries.
 *
 * The workspace lives in the URL rather than in app state so every screen is
 * linkable and two tabs can sit in two workspaces at once. Static segments are
 * declared before `/:slug`, and React Router ranks them higher regardless, so
 * `/login` can never be read as a workspace called "login" — the server also
 * reserves those words as slugs.
 *
 * `basename` must stay in step with Vite's `base`: both are `/app/`.
 */
export const router = createBrowserRouter(
  [
    {
      // Pathless root: it matches nothing itself, and exists so a single
      // errorElement covers every route in the tree.
      element: <RootLayout />,
      errorElement: <RouteErrorPage />,
      children: [
        {
          path: "/login",
          element: (
            <RequireAnonymous>
              <LoginPage />
            </RequireAnonymous>
          ),
        },
        {
          path: "/register",
          element: (
            <RequireAnonymous>
              <RegisterPage />
            </RequireAnonymous>
          ),
        },
        { path: "/forgot-password", element: <ForgotPasswordPage /> },
        { path: "/reset-password", element: <ResetPasswordPage /> },
        // Public on purpose: the preview is what makes the sign-in prompt legible.
        { path: "/invite/:token", element: <InvitePage /> },

        {
          element: <RequireAuth />,
          children: [
            { index: true, element: <WorkspaceRedirect /> },
            { path: "/invitations", element: <InvitationsPage /> },
            { path: "/new-workspace", element: <NewWorkspacePage /> },
            {
              path: "/billing/simulated-checkout",
              element: <SimulatedCheckoutPage />,
            },
            {
              path: "/billing/simulated-portal",
              element: <SimulatedPortalPage />,
            },

            // The platform admin console. It sits OUTSIDE `/:slug` because it
            // reads across every tenant rather than inside one workspace, and
            // it is declared before `/:slug` so `admin` can never be resolved
            // as a workspace slug (React Router ranks the static segment
            // higher regardless, and the server reserves the word as a slug).
            //
            // RequirePlatformAdmin renders the ordinary 404 page for everyone
            // else, matching the API, which answers 404 rather than 403 on
            // every admin route so the console cannot be found by probing.
            {
              path: "/admin",
              element: <RequirePlatformAdmin />,
              children: [
                {
                  element: <AdminLayout />,
                  children: [
                    { index: true, element: <AdminOverviewPage /> },
                    { path: "workspaces", element: <AdminWorkspacesPage /> },
                    { path: "users", element: <AdminUsersPage /> },
                    { path: "audit", element: <AdminAuditPage /> },
                  ],
                },
              ],
            },
            {
              path: "/:slug",
              element: <WorkspaceGate />,
              children: [
                {
                  element: <AppLayout />,
                  children: [
                    { index: true, element: <AppHome /> },
                    { path: "projects/:projectId", element: <ProjectPage /> },
                    // The board, backlog and issue screens read their own route
                    // params (`projectKey`, `issueKey`) and resolve the slug
                    // from the path, so they mount directly under /:slug.
                    {
                      path: "projects/:projectKey/board",
                      element: <BoardPage />,
                    },
                    {
                      path: "projects/:projectKey/backlog",
                      element: <BacklogPage />,
                    },
                    {
                      path: "projects/:projectKey/reports",
                      element: <ReportsPage />,
                    },
                    // Project settings sits beside the board rather than under
                    // /settings, because it configures ONE project — its
                    // columns, its lead — and the board's gear is the only
                    // place anyone looks for it.
                    {
                      path: "projects/:projectKey/settings",
                      element: <ProjectSettingsPage />,
                    },
                    { path: "issues/:issueKey", element: <IssuePage /> },
                    // Workspace chat: direct messages and groups. The channel
                    // id is optional so /chat lands on the inbox.
                    { path: "chat", element: <ChatPage /> },
                    { path: "chat/:channelId", element: <ChatPage /> },
                    { path: "notifications", element: <NotificationsPage /> },
                    {
                      path: "settings",
                      element: <SettingsLayout />,
                      children: [
                        {
                          index: true,
                          element: <Navigate to="general" replace />,
                        },
                        { path: "general", element: <GeneralSettingsPage /> },
                        { path: "members", element: <MembersPage /> },
                        { path: "labels", element: <LabelsPage /> },
                        { path: "ai", element: <AIPage /> },
                        { path: "voice", element: <VoiceSettingsPage /> },
                        {
                          path: "notifications",
                          element: <NotificationSettingsPage />,
                        },
                        { path: "billing", element: <BillingPage /> },
                        { path: "account", element: <AccountPage /> },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },

        { path: "*", element: <NotFound /> },
      ],
    },
  ],
  { basename: "/app" },
);
